import { ChevronRightIcon } from '@/components/atoms/icons/ChevronRightIcon';
import { UserXIcon } from '@/components/atoms/icons/UserXIcon'
import { Switch } from '@/components/atoms/Switch';
import { Typography } from '@/components/atoms/Typography';
import { ConfirmationDialog } from '@/components/molecules/dialogs/ConfirmationDialog';
import { OkDialog } from '@/components/molecules/dialogs/OkDialog';
import { SuccessDialog } from '@/components/molecules/dialogs/SuccessDialog';
import { LogoHeader } from '@/components/molecules/header/LogoHeader';
import { DT_SCREEN } from '@/core/constants/dynatraceScreens';
import { useCatalogDynatraceView } from '@/core/hooks/useCatalogDynatraceView';
import { useTranslation, useUIColors } from '@/core/hooks';
import { trackCatalogEvent } from '@/core/services/trackDynatraceCatalog';
import { trackDynatraceEvent } from '@/core/services/dynatraceService';
import { logger } from '@/core/logger';
import { useI18nContext } from '@/core/providers/I18nProvider';
import { passcodeStorage } from '@/core/storage/passcodeStorage';
import { biometricAuth } from '@/core/wrappers/biometricAuth';
import { usePasscode } from '@/modules/passcode';
import { useDeleteAccount } from '@/modules/auth/hooks/useDeleteAccount';
import { useFocusEffect } from '@react-navigation/native';
import { router } from 'expo-router';
import React, { useCallback, useEffect, useState } from 'react';
import { Platform, ScrollView, StyleSheet, TouchableOpacity, View } from 'react-native';

export function PrivacySecurityScreen() {
    useCatalogDynatraceView(DT_SCREEN.PRIVACY_SECURITY, 'privacy_security_viewed', true);
    const { t } = useTranslation();
    const colors = useUIColors();
    const { isRTL } = useI18nContext();
    const { checkPasscodeState, skipPasscode, isLoading: passcodeLoading } = usePasscode();
    const [passcodeEnabled, setPasscodeEnabled] = useState(false);
    const [biometricEnabled, setBiometricEnabled] = useState(false);
    const [biometricAvailable, setBiometricAvailable] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [confirmDialogVisible, setConfirmDialogVisible] = useState(false);
    const [biometricConfirmDialogVisible, setBiometricConfirmDialogVisible] = useState(false);
    const [changePasswordConfirmDialogVisible, setChangePasswordConfirmDialogVisible] = useState(false);
    const [deactivateConfirmDialogVisible, setDeactivateConfirmDialogVisible] = useState(false);
    const { deleteAccount, isLoading: deleteLoading, error: deleteError } = useDeleteAccount();
    const [errorDialogVisible, setErrorDialogVisible] = useState(false);
    const [errorDialogMessage, setErrorDialogMessage] = useState('');
    const [successDialogVisible, setSuccessDialogVisible] = useState(false);
    const [biometricSuccessDialogVisible, setBiometricSuccessDialogVisible] = useState(false);
    const [biometricEnabledSuccessDialogVisible, setBiometricEnabledSuccessDialogVisible] = useState(false);

    // Check biometric availability on mount
    useEffect(() => {
        const checkBiometric = async () => {
            try {
                const available = await biometricAuth.isBiometricAvailable();
                setBiometricAvailable(available);
            } catch (error) {
                logger.error('PrivacySecurity:Failed to check biometric availability', error);
            }
        };
        void checkBiometric();
    }, []);

    // Load passcode and biometric state on mount
    useEffect(() => {
        const loadPasscodeState = async () => {
            try {
                const isSet = await checkPasscodeState();
                setPasscodeEnabled(isSet);
                if (isSet) {
                    const biometric = await passcodeStorage.getBiometricEnabled();
                    setBiometricEnabled(biometric);
                }
            } catch (error) {
                logger.error('PrivacySecurity:Failed to load passcode state', error);
            } finally {
                setIsLoading(false);
            }
        };

        void loadPasscodeState();
    }, [checkPasscodeState]);

    // Reload passcode and biometric state when screen comes into focus
    useFocusEffect(
        useCallback(() => {
            const loadState = async () => {
                try {
                    const isSet = await checkPasscodeState();
                    setPasscodeEnabled(isSet);
                    if (isSet) {
                        const biometric = await passcodeStorage.getBiometricEnabled();
                        setBiometricEnabled(biometric);
                    } else {
                        setBiometricEnabled(false);
                    }
                } catch (error) {
                    logger.error('PrivacySecurity:Failed to reload passcode state', error);
                }
            };
            void loadState();
        }, [checkPasscodeState])
    );

    const handlePasscodeToggle = (newValue: boolean) => {
        if (newValue) {
            trackCatalogEvent({
                screen: DT_SCREEN.PRIVACY_SECURITY,
                actionName: 'change_passcode_started',
                eventType: 'action',
            });
            // Turning ON - navigate to setup screen
            router.push({
                pathname: '/passcode' as any,
                params: { mode: 'setup', from: 'privacy' },
            });
        } else {
            // Turning OFF - show confirmation dialog
            setConfirmDialogVisible(true);
        }
    };

    const handleConfirmDisable = async () => {
        setConfirmDialogVisible(false);
        try {
            // Call skip passcode API first with 'profile' context (per backend enum)
            await skipPasscode('profile');

            // On success, update local state
            setPasscodeEnabled(false);
            setBiometricEnabled(false);
            logger.info('PrivacySecurity:Passcode disabled via skipPasscode');

            // Show success dialog
            setSuccessDialogVisible(true);
        } catch (error) {
            logger.error('PrivacySecurity:Failed to disable passcode via skipPasscode', error);
            setErrorDialogMessage(t('profile.passcodeSkipError'));
            setErrorDialogVisible(true);
            // Revert switch to ON
            setPasscodeEnabled(true);
        }
    };

    const handleCancelDisable = () => {
        setConfirmDialogVisible(false);
        // Keep switch ON
        setPasscodeEnabled(true);
    };

    const handleChangePasscode = () => {
        logger.info('PrivacySecurity', 'Change passcode pressed');
        trackCatalogEvent({
            screen: DT_SCREEN.PRIVACY_SECURITY,
            actionName: 'change_passcode_started',
            eventType: 'action',
        });
        router.push({
            pathname: '/passcode' as any,
            params: { mode: 'change', from: 'privacy' },
        });
    };

    const handleEnableBiometric = async () => {
        if (!biometricAvailable) {
            setErrorDialogMessage('Biometric authentication is not available on this device');
            setErrorDialogVisible(true);
            return;
        }

        try {
            // Invoke biometric authentication
            const result = await biometricAuth.authenticateWithBiometric();
            if (result.success) {
                // Save biometric state
                await passcodeStorage.setBiometricEnabled(true);
                setBiometricEnabled(true);
                logger.info('PrivacySecurity:Biometric enabled');
                // Show success dialog
                setBiometricEnabledSuccessDialogVisible(true);
            } else {
                // Show error if authentication failed
                setErrorDialogMessage(result.error || t('profile.passcodeVerifyError'));
                setErrorDialogVisible(true);
            }
        } catch (error) {
            logger.error('PrivacySecurity:Failed to enable biometric', error);
            setErrorDialogMessage(t('common.error') + ': Failed to enable biometrics');
            setErrorDialogVisible(true);
        }
    };

    const handleRemoveBiometric = () => {
        // Show confirmation dialog
        setBiometricConfirmDialogVisible(true);
    };

    const handleConfirmRemoveBiometric = async () => {
        setBiometricConfirmDialogVisible(false);
        try {
            await passcodeStorage.setBiometricEnabled(false);
            // Clear stored passcode when biometric is disabled
            await passcodeStorage.clearPasscode();
            setBiometricEnabled(false);
            logger.info('PrivacySecurity:Biometric disabled');
            // Show success dialog
            setBiometricSuccessDialogVisible(true);
        } catch (error) {
            logger.error('PrivacySecurity:Failed to remove biometric', error);
            setErrorDialogMessage(t('common.error') + ': Failed to remove biometrics');
            setErrorDialogVisible(true);
        }
    };

    const handleCancelRemoveBiometric = () => {
        setBiometricConfirmDialogVisible(false);
    };

    const onChangePassword = () => {
        logger.info('PrivacySecurity', 'Change password pressed');
        setChangePasswordConfirmDialogVisible(true);
    };

    const handleConfirmChangePassword = () => {
        setChangePasswordConfirmDialogVisible(false);
        router.push('/change-password?from=profile' as any);
    };

    const onDeactivate = () => {
        logger.info('PrivacySecurity', 'Deactivate account pressed');
        trackDynatraceEvent({
            p_screen: 'profile',
            p_actionName: 'Delete Account Button Pressed',
            eventType: 'action',
        });
        setDeactivateConfirmDialogVisible(true);
    };

    const handleConfirmDeactivate = async () => {
        setDeactivateConfirmDialogVisible(false);
        const success = await deleteAccount();
        if (!success) {
            setErrorDialogMessage(deleteError || t('common.error'));
            setErrorDialogVisible(true);
        }
    };

    const handleCancelDeactivate = () => {
        setDeactivateConfirmDialogVisible(false);
    };

    const PillSwitchRow = ({
        label,
        value,
        onToggle,
        disabled,
    }: {
        label: string;
        value: boolean;
        onToggle: (value: boolean) => void;
        disabled?: boolean;
    }) => (
        <View
            style={{
                backgroundColor: colors.backgroundLevel2,
                borderRadius: 12,
                paddingHorizontal: 16,
                paddingVertical: 14,
                flexDirection: 'row',
                alignItems: 'center',
                gap: 12,
            }}
        >
            <Typography variant="headline" color="primary" style={{ flex: 1 }}>
                {label}
            </Typography>
            <View style={{ paddingVertical: 2 }}>
                <Switch value={value} onValueChange={onToggle} disabled={disabled ?? false} />
            </View>
        </View>
    );

    const PillLinkRow = ({
        label,
        onPress,
        Icon = ChevronRightIcon,
        iconColor,
    }: {
        label: string;
        onPress: () => void;
        Icon?: React.ComponentType<{ size?: number; color?: string }>;
        iconColor?: string;
    }) => (
        <TouchableOpacity
            activeOpacity={0.8}
            onPress={onPress}
            style={{
                backgroundColor: colors.backgroundLevel2,
                borderRadius: 12,
                paddingHorizontal: 16,
                paddingVertical: 14,
                flexDirection: 'row',
                alignItems: 'center',
                gap: 12,
            }}
        >
            <Typography variant="headline" color="primary" style={{ flex: 1 }}>
                {label}
            </Typography>
            <View style={{ transform: [{ scaleX: (Icon === ChevronRightIcon && isRTL) ? -1 : 1 }] }}>
                <Icon size={20} color={iconColor ?? colors.fontPrimary} />
            </View>
        </TouchableOpacity>
    );

    return (
        <View style={[styles.container, { backgroundColor: colors.backgroundLevel1 }]}>
            <LogoHeader showLanguageSwitch={false} hideLogo={true} />

            <ScrollView
                style={styles.scroll}
                contentContainerStyle={[
                    styles.content,
                    { paddingHorizontal: 16 }
                ]}
                showsVerticalScrollIndicator={false}
            >
                <Typography variant="title1" color="primary" >
                    {t('profile.privacyTitle')}
                </Typography>

                {/* Passcode Section */}
                <View style={styles.sectionGroup}>
                    <Typography variant="subhead" color="primary" style={styles.sectionLabel}>
                        {t('profile.privacyPasscodeSection')}
                    </Typography>
                    <View style={styles.sectionItems}>
                        <PillSwitchRow
                            label={t('profile.privacyPasscode')}
                            value={passcodeEnabled}
                            onToggle={handlePasscodeToggle}
                            disabled={isLoading || passcodeLoading}
                        />
                        {/* Change Passcode - only shown when passcode is enabled */}
                        {passcodeEnabled && (
                            <PillLinkRow
                                label={t('profile.privacyChangePasscode')}
                                onPress={handleChangePasscode}
                            />
                        )}
                        {/* Enable Biometric - only shown when passcode is enabled, biometric is available, and biometric is not enabled */}
                        {passcodeEnabled && biometricAvailable && !biometricEnabled && (
                            <PillLinkRow
                                label={t('profile.privacyEnableBiometric')}
                                onPress={handleEnableBiometric}
                            />
                        )}
                        {/* Remove Biometric - only shown when passcode is enabled and biometric is enabled */}
                        {passcodeEnabled && biometricEnabled && (
                            <PillLinkRow
                                label={t('profile.privacyRemoveBiometric')}
                                onPress={handleRemoveBiometric}
                            />
                        )}
                    </View>
                </View>

                {/* Login Section */}
                <View style={styles.sectionGroup}>
                    <Typography variant="subhead" color="primary" style={styles.sectionLabel}>
                        {t('profile.privacyLoginSection')}
                    </Typography>
                    <View style={styles.sectionItems}>
                        <PillLinkRow
                            label={t('profile.privacyChangePassword')}
                            onPress={onChangePassword}
                        />
                    </View>
                </View>

                {/* Deactivate Section - mobile only */}
                {Platform.OS !== 'web' && (
                    <View style={styles.sectionGroup}>
                        <Typography variant="subhead" color="primary" style={styles.sectionLabel}>
                            
                            {t('profile.deactivate' as any)}
                        </Typography>
                        <View style={styles.sectionItems}>
                            <PillLinkRow
                                label={t('profile.deactivateAccount' as any)}
                                onPress={onDeactivate}
                                Icon={UserXIcon}
                                iconColor={colors.fontErrorText}
                            />
                        </View>
                    </View>
                )}
            </ScrollView>

            {/* Confirmation Dialog */}
            <ConfirmationDialog
                visible={confirmDialogVisible}
                message={t('profile.passcodeRemoveConfirmation')}
                confirmText={t('common.yes')}
                cancelText={t('common.no')}
                onConfirm={handleConfirmDisable}
                onCancel={handleCancelDisable}
            />

            {/* Passcode Removal Success Dialog */}
            <SuccessDialog
                visible={successDialogVisible}
                title={t('profile.passcodeRemovedTitle')}
                message={t('profile.passcodeRemovedMessage')}
                onAutoClose={() => setSuccessDialogVisible(false)}
            />

            {/* Biometric Removal Confirmation Dialog */}
            <ConfirmationDialog
                visible={biometricConfirmDialogVisible}
                message={t('profile.biometricRemoveConfirmation')}
                confirmText={t('common.yes')}
                cancelText={t('common.no')}
                onConfirm={handleConfirmRemoveBiometric}
                onCancel={handleCancelRemoveBiometric}
            />

            {/* Biometric Removal Success Dialog */}
            <SuccessDialog
                visible={biometricSuccessDialogVisible}
                title={t('profile.biometricRemovedTitle')}
                message={t('profile.biometricRemovedMessage')}
                onAutoClose={() => setBiometricSuccessDialogVisible(false)}
            />

            {/* Biometric Enabled Success Dialog */}
            <SuccessDialog
                visible={biometricEnabledSuccessDialogVisible}
                title={t('profile.biometricEnabledTitle')}
                message={t('profile.biometricEnabledMessage')}
                onAutoClose={() => setBiometricEnabledSuccessDialogVisible(false)}
            />

            {/* Error Dialog */}
            <OkDialog
                visible={errorDialogVisible}
                message={errorDialogMessage}
                onOk={() => setErrorDialogVisible(false)}
            />

            {/* Change Password Confirmation Dialog */}
            <ConfirmationDialog
                visible={changePasswordConfirmDialogVisible}
                title={t('auth.changePasswordConfirmationTitle')}
                message={t('auth.changePasswordConfirmationMessage')}
                confirmText={t('common.yes')}
                cancelText={t('common.no')}
                onConfirm={handleConfirmChangePassword}
                onCancel={() => setChangePasswordConfirmDialogVisible(false)}
            />

            {/* Deactivate Account Confirmation Dialog */}
            <ConfirmationDialog
                visible={deactivateConfirmDialogVisible}
                title={t('profile.deactivateConfirmationTitle' as any)}
                message={t('profile.deactivateDescription' as any)}
                confirmText={t('common.yes')}
                cancelText={t('common.no')}
                onConfirm={handleConfirmDeactivate}
                onCancel={handleCancelDeactivate}
            />
        </View>
    );
}

const styles = StyleSheet.create({
    container: { flex: 1 },
    scroll: { flex: 1 },
    content: { paddingTop: 16, gap: 32 },
    sectionGroup: {
        gap: 8,
    },
    sectionItems: {
        gap: 12,
    },
    sectionLabel: { marginTop: 8, marginBottom: 0 },
    sectionTitle: { marginTop: 8, marginBottom: 4, fontWeight: '500' },
    list: { padding: 16, gap: 16 },
    row: { alignItems: 'center', justifyContent: 'space-between' },
    rowText: { flex: 1, gap: 2, paddingRight: 12, paddingLeft: 12 },
    separator: { height: 1 },
});

export default PrivacySecurityScreen;
